// src/pages/Agents/agentCreatePayload.ts — the document the creation wizard
// sends, and the reason each mode-bearing field has the value it has.
//
// This lives outside the component so the payload can be asserted directly:
// what the wizard SENDS is the whole contract with the backend, and the
// failure it is written to prevent was invisible in the UI.
//
// ── The failure ──
// Picking "Voice — Realtime" used to send:
//
//     agent_type:      'gemini_realtime'     (the OUTER voicex label)
//     agent_flow_type: 'streaming'           (the wizard's default, unchanged)
//     llm_agent_type:  (never sent)
//
// and the backend decides the shape of the document from neither of the first
// two the way it looks. AiAgentService.isRealtimeFlow is
//
//     str(or(b.get("agent_flow_type"), b.get("llm_agent_type"))).contains("realtime")
//
// — it does not read agent_type at all. So 'streaming' won, isRealtimeFlow
// returned false, and the agent was built as a cascaded pipeline: a deepgram
// transcriber and an elevenlabs synthesizer it never asked for, and no
// llm_config.system_instruction — which is the ONLY place a realtime agent
// reads its prompt from (voicex task_manager skips load_prompt entirely for
// this path). The agent was created, reported success, and could not work.
//
// ── What the runtime actually requires ──
// voicex TaskManager._is_gemini_realtime opts in on
// llm_agent.agent_type == "gemini_realtime" (falling back to
// llm_agent.agent_flow_type), and the backend fills llm_agent.agent_type from
// `llm_agent_type`. So llm_agent_type is not optional decoration — without it
// the runtime runs the three-layer pipeline no matter what else is set.
// agentModePatch writes both markers, which is exactly this shape.
import { AiAgent } from 'api/services/aiAgents.service';
import { Tone, toneInstruction } from './agentWizardCatalog';
import { agentModePatch, languageField, voiceField } from './agentMode';

export type Modality = 'voice_pipeline' | 'voice_realtime' | 'text';

/**
 * The Gemini Live model in production use (agent 21, adopted from the runtime).
 * voicex's GeminiRealtimeSession reads llm_config.model and talks to the Gemini
 * Live endpoint with it, so a realtime agent left on the wizard's gpt-4o
 * default is a realtime document pointed at a model that cannot serve it.
 */
export const REALTIME_MODEL = 'gemini-3.1-flash-live-preview';
/** family/provider are ignored on the realtime path, but a coherent document is worth having. */
export const REALTIME_PROVIDER = 'gemini';

export const DEFAULT_MODEL = 'gpt-4o';
export const DEFAULT_PROVIDER = 'openai';

export interface WizardState {
  templateId: string;
  modality: Modality;
  name: string;
  description: string;
  // knowledge
  knowledgeBase: string;
  enableKnowledgeBase: boolean;
  kbTopK: number;
  kbThreshold: number;
  // voice & language
  language: string;
  extraLanguages: string[];
  voice: string;
  tone: Tone;
  sttProvider: string;
  sttModel: string;
  ttsProvider: string;
  ttsVoiceId: string;
  ttsModel: string;
  // capabilities
  capabilities: Record<string, boolean>;
  // instructions
  greetingMessage: string;
  systemPrompt: string;
  memoryProfileId: number | '';
  // engine
  provider: string;
  model: string;
  temperature: number;
  maxTokens: number;
  agentFlowType: string;
  hangupAfterSilence: number | '';
  callTerminate: number | '';
  agentSpeaksFirst: boolean;
  checkIfUserOnline: '' | 'true' | 'false';
  checkUserOnlineMessage: string;
  triggerUserOnlineMessageAfter: number | '';
  numberOfWordsForInterruption: number | '';
  backchanneling: '' | 'true' | 'false';
  ambientNoise: '' | 'true' | 'false';
  ambientNoiseTrack: string;
  conversationConfigJson: string;
}

/**
 * The create body for a wizard state.
 *
 * Every non-realtime field is produced exactly as it was before this module
 * existed, in the same order and with the same blank/undefined handling, so a
 * pipeline or text agent's document is byte-for-byte what it always was.
 */
export function buildCreatePayload(
  s: WizardState,
  conversationConfig?: Record<string, any>,
): AiAgent {
  const blank = (v: string) => (v.trim() === '' ? undefined : v.trim());
  const num = (n: number | '') => (n === '' ? undefined : n);
  const tri = (v: string): boolean | undefined => (v === '' ? undefined : v === 'true');

  const isVoice = s.modality !== 'text';
  const isPipeline = s.modality === 'voice_pipeline';
  const isRealtime = s.modality === 'voice_realtime';
  // A text agent has no voice path at all; it keeps the pipeline field names,
  // which is what it has always sent.
  const mode = isRealtime ? 'realtime' : 'pipeline';

  // Tone is a real instruction rather than a label on a chip.
  const prompt = [s.systemPrompt.trim(), toneInstruction(s.tone)].filter(Boolean).join('\n\n');

  // Voice and language are two different pairs of fields, not two spellings of
  // one pair: a realtime model reads llm_config.voice_name / language_code, a
  // pipeline agent reads the synthesizer and transcriber blocks the backend
  // fills from voice / language. Sending the pipeline names for a realtime
  // agent puts them on blocks the backend sets to null.
  const voiceAndLanguage: Record<string, unknown> = {
    [languageField(mode)]: s.language,
    ...(isVoice ? { [voiceField(mode)]: s.voice } : { voice: undefined }),
  };

  // The mode markers. For realtime this is agentModePatch's realtime shape —
  // both flow fields set, because isRealtimeFlow reads agent_flow_type first
  // and llm_agent_type is what reaches llm_agent.agent_type, which is what the
  // runtime switches on. For everything else the flow type is the user's
  // choice, unchanged, and llm_agent_type is left unsent so the backend applies
  // its own 'simple_llm_agent' default exactly as before.
  //
  // "Agent flow type" (streaming / non_streaming) is not a meaningful control
  // for a realtime agent: the flow type IS the realtime marker, so both of its
  // options would silently turn the agent back into a pipeline. The wizard
  // hides it in that mode rather than sending a value that contradicts the
  // modality the user picked.
  const modeMarkers: Record<string, unknown> = isRealtime
    ? agentModePatch({}, 'realtime')
    : { agent_flow_type: isVoice ? blank(s.agentFlowType) : undefined };

  return {
    name: s.name.trim(),
    description: s.description,
    enabled: true,
    runtime: isVoice ? 'TEEOFF_VOICE' : 'TEEOFF_LLM',
    // The OUTER voicex label. It is NOT what decides the document — but it is
    // the only mode marker the agents created before this fix carry, and
    // resolveAgentMode still falls back to it for them, so it keeps its value.
    agent_type: isRealtime ? 'gemini_realtime' : 'simple_llm_agent',
    provider: s.provider,
    model: s.model,
    temperature: s.temperature,
    max_tokens: s.maxTokens,
    ...voiceAndLanguage,
    greeting_message: s.greetingMessage,
    system_prompt: prompt,
    memory_profile_id: s.memoryProfileId === '' ? undefined : s.memoryProfileId,
    stt_provider: isPipeline ? blank(s.sttProvider) : undefined,
    stt_model: isPipeline ? blank(s.sttModel) : undefined,
    tts_provider: isPipeline ? blank(s.ttsProvider) : undefined,
    tts_voice_id: isPipeline ? blank(s.ttsVoiceId) : undefined,
    tts_model: isPipeline ? blank(s.ttsModel) : undefined,
    ...modeMarkers,
    hangup_after_silence: num(s.hangupAfterSilence),
    call_terminate: num(s.callTerminate),
    agent_speaks_first: s.agentSpeaksFirst,
    check_if_user_online: tri(s.checkIfUserOnline),
    check_user_online_message: blank(s.checkUserOnlineMessage),
    trigger_user_online_message_after: num(s.triggerUserOnlineMessageAfter),
    number_of_words_for_interruption: num(s.numberOfWordsForInterruption),
    backchanneling: tri(s.backchanneling),
    ambient_noise: tri(s.ambientNoise),
    ambient_noise_track: blank(s.ambientNoiseTrack),
    conversation_config: conversationConfig,
    knowledge_base: s.knowledgeBase,
    kb_enabled: s.enableKnowledgeBase,
    kb_top_k: s.kbTopK,
    kb_similarity_threshold: s.kbThreshold,
  };
}
